import type {
  NeedsActionPatient,
  NeedsActionListResponse,
  CurrentAssessment,
  AssessmentTimelineItem,
} from "../../../domain/entities/NeedsAction";
import type {
  NeedsActionPatientDTO,
  NeedsActionListResponseDTO,
  CurrentAssessmentDTO,
  AssessmentTimelineItemDTO,
} from "../../../domain/dto/NeedsActionDTO";
import { formatRelativeTimeWithDays } from "@/lib/dateUtils";


const formatEnumLabel = (value: string) => {
  return value
    .replace(/^(SCR|CC|EXAM)_/, "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const STATUS_LABELS: Record<string, string> = {
  SCR_IN_PROGRESS: "Screening - In Progress",
  SCR_DROPPED: "Screening - Dropped",
  SCR_COMPLETED: "Screening - Completed",
  SCR_FAILED: "Screening - Failed",
  SCR_REPORT_PENDING: "Screening - Report Pending",
  EXAM_IN_PROGRESS: "Clinical Exam - In Progress",
  EXAM_DROPPED: "Clinical Exam - Dropped",
  EXAM_COMPLETED: "Clinical Exam - Completed",
  EXAM_FAILED: "Clinical Exam - Failed",

};

const STATUS_TYPES: Record<string, "success" | "warning" | "error"> = {
  SCR_IN_PROGRESS: "warning",
  SCR_DROPPED: "error",
  SCR_COMPLETED: "success",
  SCR_FAILED: "error",
  SCR_REPORT_PENDING: "warning",
  EXAM_IN_PROGRESS: "warning",
  EXAM_DROPPED: "error",
  EXAM_COMPLETED: "success",
  EXAM_FAILED: "error",
};

const ACTION_LABELS: Record<string, string> = {
  COMPLETE_SCREENING: "Complete Screening",
  CREATE_NEW_ASSESSMENT: "Create New Assessment",
  REVIEW_REPORT: "Review Report",
  RETRY_SCREENING: "Retry Screening",
  COMPLETE_EXAM: "Complete Exam",
  RETRY_EXAM: "Retry Exam",
};

const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const mapCurrentAssessmentDTO = (
  dto: CurrentAssessmentDTO,
): CurrentAssessment => ({
  id: dto.id,
  status: dto.status,
  nextAction: dto.next_action,
  statusAt: dto.status_at,
});

const mapTimelineItemDTO = (
  dto: AssessmentTimelineItemDTO,
): AssessmentTimelineItem => ({
  stage: dto.stage,
  status: dto.status,
  at: dto.at,
});

export const mapNeedsActionPatientDTO = (
  dto: NeedsActionPatientDTO,
): NeedsActionPatient => ({
  id: dto.id,
  name: dto.name,
  phone: dto.phone,
  currentAssessment: mapCurrentAssessmentDTO(dto.current_assessment),
  assessmentTimeline: dto.assessment_timeline.map(mapTimelineItemDTO),
  initials: getInitials(dto.name),
  statusLabel:
    STATUS_LABELS[dto.current_assessment.status] ??
    formatEnumLabel(dto.current_assessment.status),
  statusType: STATUS_TYPES[dto.current_assessment.status] ?? "warning",
  actionLabel:
    ACTION_LABELS[dto.current_assessment.next_action] ??
    formatEnumLabel(dto.current_assessment.next_action),
  formattedStatusAt: formatRelativeTimeWithDays(
    dto.current_assessment.status_at,
  ),
});

export const mapNeedsActionListResponseDTO = (
  response: NeedsActionListResponseDTO,
): NeedsActionListResponse => ({
  items: response.data.items.map(mapNeedsActionPatientDTO),
  meta: response.meta,
});
